import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserAchievements } from '@/hooks/useUserAchievements';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { supabase } from '@/integrations/supabase/client';

// Import achievement icons
import trophyIcon from '@/assets/images/achievements/trophy.png';
import goalIcon from '@/assets/images/achievements/goal.png';
import stripePromotionIcon from '@/assets/images/achievements/stripe_promotion.png';
import beltIcon from '@/assets/images/achievements/belt.png';
import AwardIcon from '@/assets/icons/award.svg';

interface Achievement {
  id: string;
  type: 'training_goal' | 'belt_promotion' | 'stripe_promotion';
  title: string;
  description: string;
  created_at: string;
  metadata: {
    month?: string;
    trainings_count?: number;
    belt_color?: string;
    school?: string;
    ordinal_stripe?: string;
    technique_category?: string;
  };
}

const UserAchievements: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { data: achievements, isLoading, error } = useUserAchievements(userId || '');
  const { t } = useI18n();
  const { user } = useAuth();
  const { profile: myProfile } = useUserProfile();
  const [friendName, setFriendName] = useState<string>('');

  // Fetch friend's name when userId changes
  useEffect(() => {
    const fetchFriendName = async () => {
      if (userId && userId !== user?.id) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', userId)
            .single();
          
          if (data?.full_name) {
            setFriendName(data.full_name);
          }
        } catch (error) {
          console.error('Error fetching friend name:', error);
        }
      }
    };

    fetchFriendName();
  }, [userId, user?.id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAchievementIcon = (type: string) => {
    switch (type) {
      case 'training_goal':
        return goalIcon;
      case 'belt_promotion':
        return beltIcon;
      case 'stripe_promotion':
        return stripePromotionIcon;

      default:
        return trophyIcon;
    }
  };

  // Function to translate achievement title and description from database values
  const translateAchievement = (achievement: Achievement) => {
    const { type, title, description, metadata } = achievement;
    
    // Helper function to translate belt colors from English to Portuguese
    const translateBeltColor = (color: string): string => {
      const colorMap: { [key: string]: string } = {
        'white': 'branca',
        'blue': 'azul',
        'purple': 'roxa',
        'brown': 'marrom',
        'black': 'preta'
      };
      return colorMap[color.toLowerCase()] || color;
    };
    
    // Dynamic title translation based on content analysis
    let translatedTitle = title;
    
    // Analyze title content and translate dynamically
    const titlePatterns = [
      // Training goal patterns
      { pattern: /monthly/i, key: 'achievements.titles.training_goal.monthly_training' },
      { pattern: /technique/i, key: 'achievements.titles.training_goal.technique_mastery' },
      { pattern: /consistency/i, key: 'achievements.titles.training_goal.consistency_king' },
      { pattern: /endurance/i, key: 'achievements.titles.training_goal.endurance_champion' },
      { pattern: /flexibility/i, key: 'achievements.titles.training_goal.flexibility_guru' },
      { pattern: /strength/i, key: 'achievements.titles.training_goal.strength_warrior' },
      { pattern: /speed/i, key: 'achievements.titles.training_goal.speed_demon' },
      { pattern: /precision/i, key: 'achievements.titles.training_goal.precision_artist' },
      
      // Belt promotion patterns
      { pattern: /white.*blue/i, key: 'achievements.titles.belt_promotion.white_to_blue' },
      { pattern: /blue.*purple/i, key: 'achievements.titles.belt_promotion.blue_to_purple' },
      { pattern: /purple.*brown/i, key: 'achievements.titles.belt_promotion.purple_to_brown' },
      { pattern: /brown.*black/i, key: 'achievements.titles.belt_promotion.brown_to_black' },
      { pattern: /black.*belt/i, key: 'achievements.titles.belt_promotion.black_belt' },
      
      // Stripe promotion patterns
      { pattern: /first.*stripe/i, key: 'achievements.titles.stripe_promotion.first_stripe' },
      { pattern: /second.*stripe/i, key: 'achievements.titles.stripe_promotion.second_stripe' },
      { pattern: /third.*stripe/i, key: 'achievements.titles.stripe_promotion.third_stripe' },
      { pattern: /fourth.*stripe/i, key: 'achievements.titles.stripe_promotion.fourth_stripe' },
      
      
      // Exact title matches from database
      { pattern: /^stripe promotion!$/i, key: 'achievements.titles.stripe_promotion.general' },
      { pattern: /^belt promotion!$/i, key: 'achievements.titles.belt_promotion.general' },
    ];
    
    // Find matching pattern and translate
    for (const { pattern, key } of titlePatterns) {
      if (pattern.test(title)) {
        translatedTitle = t(key);
        break;
      }
    }
    
    // Dynamic description translation based on type and content
    let translatedDescription = description;
    
    if (type === 'training_goal') {
      // Extract numbers and values from description
      const numbers = description.match(/\d+/g);
      const count = numbers?.[0] || '15';
      
      if (description.toLowerCase().includes('trainings') || description.toLowerCase().includes('count')) {
        translatedDescription = t('achievements.descriptions.training_goal.monthly_training', { count });
      } else if (description.toLowerCase().includes('technique') || description.toLowerCase().includes('mastered')) {
        const technique = metadata?.technique_category || 'técnica';
        translatedDescription = t('achievements.descriptions.training_goal.technique_mastery', { technique });
      } else if (description.toLowerCase().includes('consecutive') || description.toLowerCase().includes('days')) {
        const days = numbers?.[0] || '30';
        translatedDescription = t('achievements.descriptions.training_goal.consistency_king', { days });
      } else if (description.toLowerCase().includes('endurance')) {
        translatedDescription = t('achievements.descriptions.training_goal.endurance_champion', { duration: '2 horas' });
      } else if (description.toLowerCase().includes('flexibility')) {
        translatedDescription = t('achievements.descriptions.training_goal.flexibility_guru');
      } else if (description.toLowerCase().includes('strength')) {
        const percentage = numbers?.[0] || '25';
        translatedDescription = t('achievements.descriptions.training_goal.strength_warrior', { percentage });
      } else if (description.toLowerCase().includes('speed')) {
        translatedDescription = t('achievements.descriptions.training_goal.speed_demon', { time: '0.5s' });
      } else if (description.toLowerCase().includes('precision')) {
        const accuracy = numbers?.[0] || '95';
        translatedDescription = t('achievements.descriptions.training_goal.precision_artist', { accuracy });
      }
    } else if (type === 'belt_promotion') {
      if (description.toLowerCase().includes('white to blue')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.white_to_blue');
      } else if (description.toLowerCase().includes('blue to purple')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.blue_to_purple');
      } else if (description.toLowerCase().includes('purple to brown')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.purple_to_brown');
      } else if (description.toLowerCase().includes('brown to black')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.brown_to_black');
      } else if (description.toLowerCase().includes('black belt')) {
        translatedDescription = t('achievements.descriptions.belt_promotion.black_belt');
      } else if (description.toLowerCase().includes('conquered')) {
        // Extract belt color and user name from description like "Eduardo Silva conquered the purple belt!"
        const beltMatch = description.match(/(.+) conquered the (\w+) belt/i);
        if (beltMatch) {
          const userName = beltMatch[1].trim();
          const beltColor = translateBeltColor(beltMatch[2]);
          if (beltMatch[2].toLowerCase() === 'white') {
            translatedDescription = t('achievements.descriptions.belt_promotion.white_belt', { name: userName });
          } else if (beltMatch[2].toLowerCase() === 'blue') {
            translatedDescription = t('achievements.descriptions.belt_promotion.blue_belt', { name: userName });
          } else if (beltMatch[2].toLowerCase() === 'purple') {
            translatedDescription = t('achievements.descriptions.belt_promotion.purple_belt', { name: userName });
          } else if (beltMatch[2].toLowerCase() === 'brown') {
            translatedDescription = t('achievements.descriptions.belt_promotion.brown_belt', { name: userName });
          } else if (beltMatch[2].toLowerCase() === 'black') {
            translatedDescription = t('achievements.descriptions.belt_promotion.black_belt', { name: userName });
          }
        }
      }
    } else if (type === 'stripe_promotion') {
      const beltColor = metadata?.belt_color || 'faixa';
      if (description.toLowerCase().includes('first stripe')) {
        translatedDescription = t('achievements.descriptions.stripe_promotion.first_stripe', { belt: beltColor });
      } else if (description.toLowerCase().includes('second stripe')) {
        translatedDescription = t('achievements.descriptions.stripe_promotion.second_stripe', { belt: beltColor });
      } else if (description.toLowerCase().includes('third stripe')) {
        translatedDescription = t('achievements.descriptions.stripe_promotion.third_stripe', { belt: beltColor });
      } else if (description.toLowerCase().includes('fourth stripe')) {
        translatedDescription = t('achievements.descriptions.stripe_promotion.fourth_stripe', { belt: beltColor });
      } else if (description.toLowerCase().includes('earned their')) {
        // Extract stripe number, belt color and user name from description like "Eduardo Silva has just earned their 2nd brown belt stripe."
        const stripeMatch = description.match(/(.+) has just earned their (\d+)(?:st|nd|rd|th) (\w+) belt stripe/i);
        if (stripeMatch) {
          const userName = stripeMatch[1].trim();
          const stripeNumber = parseInt(stripeMatch[2]);
          const beltColor = translateBeltColor(stripeMatch[3]);
          
          let stripeKey = 'first_stripe';
          if (stripeNumber === 2) stripeKey = 'second_stripe';
          else if (stripeNumber === 3) stripeKey = 'third_stripe';
          else if (stripeNumber === 4) stripeKey = 'fourth_stripe';
          
          translatedDescription = t(`achievements.descriptions.stripe_promotion.${stripeKey}`, { 
            name: userName, 
            belt: beltColor 
          });
        }
      }
    }
    
    return {
      title: translatedTitle,
      description: translatedDescription
    };
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#160C0C] text-white font-roboto">
        <TopNavigation variant="subtle" />
        <div className="pt-16">
          {/* Loading skeleton */}
          <div className="p-4">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#231A1A] rounded-2xl p-4 border border-[#3A2A2A] animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#3A2A2A] rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-[#3A2A2A] rounded mb-2"></div>
                      <div className="h-3 bg-[#3A2A2A] rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#160C0C] text-white font-roboto">
        <TopNavigation variant="subtle" />
        <div className="pt-16">
          <div className="flex items-center justify-between p-4 border-b border-[#281B1B]">
            <button onClick={handleBack} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">{t('achievements.back')}</span>
            </button>
            <h1 className="text-lg font-semibold">{t('achievements.page_title')}</h1>
            <div className="w-20"></div>
          </div>
          <div className="p-4">
            <p className="text-white/60">{t('achievements.error_loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#160C0C] text-white font-roboto">
      <TopNavigation variant="subtle" />
      
      {/* Header */}
      <div className="pt-16">
        <div className="flex items-center justify-between p-4 border-b border-[#281B1B]">
          <button onClick={handleBack} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{t('achievements.back')}</span>
          </button>
          <h1 className="text-lg font-semibold">{t('achievements.page_title')}</h1>
          <div className="w-20"></div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Total count - Only show when there are achievements */}
          {achievements && achievements.length > 0 && (
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[14px] font-bold">{t('achievements.all_achievements')}</h2>
              <div className="text-white text-sm font-normal">{t('achievements.total')}: {achievements.length}</div>
            </div>
          )}

          {/* Achievements list */}
          {achievements && achievements.length > 0 ? (
            <div className="space-y-4">
              {achievements.map((achievement) => {
                const { title, description } = translateAchievement(achievement);
                
                return (
                  <div key={achievement.id} className="bg-[#231A1A] rounded-2xl p-4 border border-[#3A2A2A] relative">
                    {/* Date in top right corner */}
                    <div className="absolute top-4 right-4">
                      <span className="text-white/60 text-xs">
                        {formatDate(achievement.created_at)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 pr-16">
                      {/* Achievement icon */}
                      <div className="flex-shrink-0">
                        <img
                          src={getAchievementIcon(achievement.type)}
                          alt={title}
                          className="w-10 h-10 max-h-[40px] rounded"
                        />
                      </div>

                      {/* Achievement content */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold text-[12px] mb-1">
                          {title}
                        </h3>
                        <p className="text-white/80 text-[10px]">
                          {description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
              {/* Empty State Icon */}
              <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                <img 
                  src={AwardIcon} 
                  alt={t('achievements.page_title')} 
                  className="w-10 h-10 text-white/40"
                  style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
                />
              </div>
              
              {/* Empty State Title */}
              <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                {t('achievements.no_achievements_yet')}
              </h3>
              
              {/* Empty State Subtitle */}
              <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                {user?.id === userId ? (
                  // My profile
                  t('achievements.no_achievements_subtitle')
                ) : (
                  // Other user's profile
                  `Invite ${friendName || 'your friend'} to roll with you and help to earn achievements!`
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAchievements; 